import { Injectable } from '@angular/core';
import {
	PluginType,
	PluginWordMazeGameData,
	PluginInterfaceId,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';
import { WordMazeGridGeneratorService } from '../word-maze-grid-generator.service';

/**
 * Word Maze specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class WordMazeGenerationService extends BaseGenerationService<PluginWordMazeGameData> {
	readonly pluginType = PluginType.WordMaze;

	constructor(private gridGenerator: WordMazeGridGeneratorService) {
		super();
	}

	/**
	 * Validate generation request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		// For source material generation
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.fileHandles.length > 0;

		// For theme-based generation
		const hasTheme =
			request.parameters['theme'] &&
			request.parameters['theme'].trim().length > 0;

		if (!hasSourceMaterial && !hasTheme) {
			return {
				valid: false,
				error: 'Anna teema tai valitse lähdemateriaali',
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for word maze generation
	 */
	getDefaultParameters(): any {
		return {
			theme: '', // Empty by default - allows source material to be used
			wordCount: 9,
			gridSize: 12,
			language: 'fi',
			clearPrevious: false,
		};
	}

	/**
	 * Create system and user prompts for word maze generation
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters, sourceMaterial } = request;
		const wordCount = parameters['wordCount'] || 9;
		const gridSize = parameters['gridSize'] || 12;

		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		let systemPrompt: string;
		let userPrompt: string;

		if (parameters['theme']?.trim()) {
			// Theme-based generation
			const theme = parameters['theme'];

			systemPrompt = `Olet sanaristikko- ja sanahakupelien luoja. Tehtäväsi on luoda Sanasokkelo-peli, jossa pelaaja etsii sanoja ruudukosta.

SÄÄNNÖT:
- Luo ${wordCount} sanaa teemaan liittyen
- Sanojen pituus: 3-12 kirjainta (suosi pituuksia 4-8)
- Kaikki sanat samasta teemasta
- Sanat voivat olla substantiiveja, verbejä tai adjektiiveja
- Vältä liian vaikeita tai harvinaisia sanoja
- Vältä sanojen sisältyvyyttä (esim. "KISSA" ja "KISSANRUOKA")

TÄRKEÄÄ - KIRJOITUSASU JA MERKIT:
- Käytä VAIN aakkosia (A-Ö, mukaan lukien Ä ja Ö)
- EI numeroita, väliviivoja, välilyöntejä tai erikoismerkkejä
- Esimerkki VÄÄRIN: "VAI-NAJA" (sisältää väliviivan)
- Esimerkki OIKEIN: "VAINAJA"
- Varmista että jokainen sana on kirjoitettu täysin oikein suomen kielen mukaan
- Tarkista erityisesti kaksoiskonsonantit (esim. VAMPYYRI, ei VAMPPYYRI)
- Älä lisää ylimääräisiä kirjaimia sanoihin
- Jokainen sana on oltava olemassa oleva suomen kielen sana

${targetLanguagePrompt}`;

			userPrompt = `Luo Sanasokkelo-peli teemalla "${theme}".

TÄRKEÄÄ: Palauta JSON-objekti, jossa on KAIKKI seuraavat kentät:
{
	"title": "Pelin nimi (esim. '${theme}')",
	"words": ["SANA1", "SANA2", "SANA3", ...]
}

HUOMAA:
- "title"-kentässä tulee olla lyhyt, kuvaava nimi pelille
- "words"-taulukko sisältää ${wordCount} kappaletta 3-9 kirjaimen mittaisia sanoja
- Sanat ISOILLA KIRJAIMILLA
- Älä sisällytä duplikaatteja
- Varmista että sanat liittyvät teemaan "${theme}"
- Ruudukko luodaan automaattisesti - keskity vain hyvien sanojen luomiseen!

Luo sanat teeman mukaisiksi!`;
		} else {
			// Source material-based generation
			systemPrompt = `Olet sanaristikko- ja sanahakupelien luoja. Tehtäväsi on luoda Sanasokkelo-peli, jossa pelaaja etsii sanoja ruudukosta.

Selvitä, mistä lähdemateriaalissa on kyse ja poimi sieltä keskeisiä sanoja Sanasokkelo-peliä varten.

SÄÄNNÖT:
- Luo ${wordCount} sanaa lähdemateriaaleista
- Sanojen pituus: 3-12 kirjainta (suosi pituuksia 4-8)
- Kaikki sanat valitaan lähdemateriaalista
- Sanat voivat olla avainsanoja, käsitteitä tai keskeisiä termejä
- Vältä liian vaikeita tai harvinaisia sanoja
- Vältä sanojen sisältyvyyttä (esim. "KISSA" ja "KISSANRUOKA")
- Valitse sanoja, jotka liittyvät lähdemateriaalin keskeisiin aiheisiin

TÄRKEÄÄ - KIRJOITUSASU JA MERKIT:
- Käytä VAIN aakkosia (A-Ö, mukaan lukien Ä ja Ö)
- EI numeroita, väliviivoja, välilyöntejä tai erikoismerkkejä
- Esimerkki VÄÄRIN: "VAI-NAJA" (sisältää väliviivan)
- Esimerkki OIKEIN: "VAINAJA"
- Varmista että jokainen sana on kirjoitettu täysin oikein suomen kielen mukaan
- Tarkista erityisesti kaksoiskonsonantit (esim. VAMPYYRI, ei VAMPPYYRI)
- Älä lisää ylimääräisiä kirjaimia sanoihin
- Jokainen sana on oltava olemassa oleva suomen kielen sana

${targetLanguagePrompt}`;

			userPrompt = `Luo Sanasokkelo-peli valitusta lähdemateriaalista.

TÄRKEÄÄ: Palauta JSON-objekti, jossa on KAIKKI seuraavat kentät:
{
	"title": "Lähdemateriaalin pääaihe",
	"words": ["SANA1", "SANA2", "SANA3", ...]
}

HUOMAA:
- "title"-kentässä tulee olla lyhyt, kuvaava nimi (lähdemateriaalin pääaihe)
- "words"-taulukko sisältää ${wordCount} kappaletta 3-9 kirjaimen mittaisia sanoja
- Sanat ISOILLA KIRJAIMILLA
- Älä sisällytä duplikaatteja
- Poimi sanat lähdemateriaalin keskeisistä käsitteistä
- Ruudukko luodaan automaattisesti - keskity vain hyvien sanojen valintaan!

Poimi sanat lähdemateriaalista!`;
		}

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Process AI response into PluginWordMazeGameData
	 */
	protected processResponseCore(
		response: any,
		parameters: any,
	): GenerationResult {
		try {
			const json = response.json;

			if (!json || !json.words || !Array.isArray(json.words)) {
				return {
					success: false,
					error: 'Invalid response: missing words array',
					rawResponse: response.rawResponse,
				};
			}

			// Create game data
			const gameData = new PluginWordMazeGameData();
			gameData.title = json.title || 'Sanasokkelo';
			gameData.description = 'Etsi kaikki piilotetut sanat ruudukosta!';
			gameData.gridSize = parameters['gridSize'] || 12;
			gameData.selectedInterfaceId = PluginInterfaceId.Interface1;

			// Filter and validate words
			const alphabetOnlyRegex = /^[A-ZÄÖÅ]+$/;
			const validWords = json.words
				.filter((word: any) => {
					if (typeof word !== 'string') return false;
					const trimmed = word.toUpperCase().trim();
					// Check length and alphabet-only characters
					return (
						trimmed.length >= 3 &&
						trimmed.length <= 12 &&
						alphabetOnlyRegex.test(trimmed)
					);
				})
				.map((word: string) => word.toUpperCase().trim())
				// Remove duplicates
				.filter((word: string, index: number, self: string[]) =>
					self.indexOf(word) === index
				);

			if (validWords.length === 0) {
				return {
					success: false,
					error: 'No valid words generated',
					rawResponse: response.rawResponse,
				};
			}

			// Generate grid with words
			try {
				const { grid, wordPositions } = this.gridGenerator.generateGrid(
					validWords,
					gameData.gridSize,
				);

				gameData.grid = grid;
				gameData.words = wordPositions;
			} catch (error) {
				return {
					success: false,
					error: `Grid generation failed: ${error}`,
					rawResponse: response.rawResponse,
				};
			}

			return {
				success: true,
				gameData,
				rawResponse: response.rawResponse,
			};
		} catch (error) {
			return {
				success: false,
				error: `Processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Validate generated game data
	 */
	protected validateGameData(
		gameData: PluginWordMazeGameData,
	): ValidationResult {
		// Check basic data
		if (!gameData.title || gameData.title.trim() === '') {
			return {
				valid: false,
				error: 'Missing or empty title',
			};
		}

		if (!gameData.words || gameData.words.length === 0) {
			return {
				valid: false,
				error: 'No words in puzzle',
			};
		}

		// Validate grid size
		if (![10, 12, 15].includes(gameData.gridSize)) {
			return {
				valid: false,
				error: `Invalid grid size: ${gameData.gridSize}`,
			};
		}

		// Validate grid dimensions
		if (
			!gameData.grid ||
			gameData.grid.length !== gameData.gridSize ||
			!gameData.grid.every(row => row.length === gameData.gridSize)
		) {
			return {
				valid: false,
				error: 'Grid dimensions do not match grid size',
			};
		}

		// Validate words
		const alphabetOnlyRegex = /^[A-ZÄÖÅ]+$/;
		const errors: string[] = [];
		for (const wordPos of gameData.words) {
			if (!wordPos.word || wordPos.word.length < 3 || wordPos.word.length > 12) {
				errors.push(`Invalid word length: ${wordPos.word}`);
			}

			if (!alphabetOnlyRegex.test(wordPos.word)) {
				errors.push(`Word contains invalid characters: ${wordPos.word}`);
			}

			if (!wordPos.positions || wordPos.positions.length !== wordPos.word.length) {
				errors.push(`Position mismatch for word: ${wordPos.word}`);
			}
		}

		if (errors.length > 0) {
			return {
				valid: false,
				error: errors.join(', '),
			};
		}

		return { valid: true };
	}

	/**
	 * Get example game data for preview/testing
	 */
	getExampleGameData(): PluginWordMazeGameData {
		const gameData = new PluginWordMazeGameData();
		gameData.title = 'Halloween';
		gameData.description = 'Etsi kaikki piilotetut sanat ruudukosta!';
		gameData.gridSize = 12;

		const words = [
			'NOITA',
			'KUMMITUS',
			'LUURANKO',
			'KURPITSA',
			'LEPAKKO',
			'KAUHU',
			'YÖTAIVAS',
			'KYNTTILÄ',
			'HÄMÄHÄKKI',
		];

		const { grid, wordPositions } = this.gridGenerator.generateGrid(
			words,
			gameData.gridSize,
		);

		gameData.grid = grid;
		gameData.words = wordPositions;

		return gameData;
	}
}
